import React, { useEffect, useState } from "react";
import { Modal, Form, Input, Select, Checkbox, Row, Col, Spin, message, Popconfirm, Button, Tag } from "antd";
import { EditOutlined, PhoneOutlined, MailOutlined, HomeOutlined, IdcardOutlined, FileTextOutlined } from "@ant-design/icons";

const { Option } = Select;

export type RoleType = 'CUSTOMER' | 'PROVIDER' | 'EMPLOYEE' | 'SUPPLIER' | 'PARTNER';

const roleLabels: Record<RoleType, string> = {
  CUSTOMER: 'Cliente',
  PROVIDER: 'Proveedor',
  EMPLOYEE: 'Empleado',
  SUPPLIER: 'Proveedor',
  PARTNER: 'Socio / Accionista'
};

const docTypeLabels: Record<string, string> = {
  CC: 'Cédula de ciudadanía',
  NIT: 'NIT',
};

export interface Contact {
  id?: number;
  uid?: string;
  name: string;
  document_type: string;
  document_number: string;
  address: string;
  email: string;
  phone: string;
  requires_electronic_invoice: boolean;
  roles?: { role_type: string }[];
  company_id?: number;
}

interface UnifiedContactModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: (contact: any) => void;
  initialValues?: Partial<Contact>;
  validationMode?: 'strict' | 'relaxed';
  role?: RoleType;
  loading?: boolean;
  onSave: (values: any) => Promise<any>;
}

// ─── Read-only view ───────────────────────────────────────────────────────────
const labelStyle: React.CSSProperties = {
  fontSize: 12,
  color: '#8c8c8c',
  marginBottom: 2,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
};

const valueStyle: React.CSSProperties = {
  fontSize: 14,
  color: '#262626',
  fontWeight: 500,
};

const rowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 12,
  padding: '10px 0',
  borderBottom: '1px solid #f0f0f0',
};

const blockStyle: React.CSSProperties = { flex: 1 };

interface ViewContactProps {
  contact: Partial<Contact>;
  onEdit: () => void;
}

const ViewContact: React.FC<ViewContactProps> = ({ contact, onEdit }) => {
  const isEmpty = (v?: string | null) => !v || v.trim() === '';

  const InfoRow = ({
    icon,
    label,
    value,
    children,
  }: {
    icon: React.ReactNode;
    label: string;
    value?: string;
    children?: React.ReactNode;
  }) => (
    <div style={rowStyle}>
      <div style={{ paddingTop: 2, color: '#bfbfbf', flexShrink: 0 }}>{icon}</div>
      <div style={blockStyle}>
        <div style={labelStyle}>{label}</div>
        {children ?? <div style={valueStyle}>{isEmpty(value) ? <span style={{ color: '#bfbfbf' }}>—</span> : value}</div>}
      </div>
    </div>
  );

  return (
    <div style={{ padding: '0 4px 8px' }}>
      {/* Name header */}
      <div style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 20, fontWeight: 600, color: '#262626' }}>
          {contact.name || '—'}
        </div>
      </div>

      {/* Detail card */}
      <div style={{ background: '#fafafa', borderRadius: 8, padding: '4px 16px', marginBottom: 20 }}>
        <InfoRow icon={<IdcardOutlined />} label="Documento">
          <div style={valueStyle}>
            {contact.document_type
              ? `${docTypeLabels[contact.document_type] ?? contact.document_type} · ${contact.document_number ?? '—'}`
              : <span style={{ color: '#bfbfbf' }}>—</span>}
          </div>
        </InfoRow>

        <InfoRow icon={<PhoneOutlined />} label="Celular" value={contact.phone} />

        <InfoRow icon={<MailOutlined />} label="Email" value={contact.email} />

        <InfoRow icon={<HomeOutlined />} label="Dirección" value={contact.address} />

        <InfoRow icon={<FileTextOutlined />} label="Facturación electrónica">
          <div>
            {contact.requires_electronic_invoice
              ? <Tag color="blue">Obligado a facturar electrónicamente</Tag>
              : <span style={{ ...valueStyle, color: '#8c8c8c' }}>No aplica</span>}
          </div>
        </InfoRow>
      </div>

      {/* Edit button */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Button type="primary" icon={<EditOutlined />} onClick={onEdit}>
          Editar
        </Button>
      </div>
    </div>
  );
};

const getModalTitle = (isExisting: boolean, isForm: boolean, role?: RoleType): string => {
  const roleLabel = role ? roleLabels[role] : undefined;
  const suffix = roleLabel ? ` · ${roleLabel}` : '';

  if (!isExisting) return `Nuevo Contacto${suffix}`;
  if (isForm)     return `Editar Contacto${suffix}`;
  return `Contacto${suffix}`;
};

// ─── Main modal component ─────────────────────────────────────────────────────
export const UnifiedContactModal: React.FC<UnifiedContactModalProps> = ({
  visible,
  onCancel,
  onSuccess,
  initialValues,
  validationMode = 'strict',
  role,
  loading: externalLoading,
  onSave
}) => {
  const [form] = Form.useForm();
  const [localLoading, setLocalLoading] = useState(false);
  const [isFormMode, setIsFormMode] = useState(false);

  const loading = externalLoading || localLoading;
  const isExistingContact = !!(initialValues?.uid || (initialValues as any)?.uidUser);

  useEffect(() => {
    if (visible) {
      form.resetFields();
      if (initialValues) {
        form.setFieldsValue(initialValues);
      }
      // New contact → go straight to form; existing → show view first
      setIsFormMode(!isExistingContact);
    }
  }, [visible, initialValues]);

  const handleFinish = async (values: any) => {
    setLocalLoading(true);
    try {
      const mergedValues = { ...initialValues, ...values };
      if (!mergedValues.uid && mergedValues.uidUser) {
        mergedValues.uid = mergedValues.uidUser;
      }
      const result = await onSave(mergedValues);
      onSuccess(result ?? mergedValues);
      form.resetFields();
    } catch (error: any) {
      message.error(error.message || "Error al guardar el contacto");
    } finally {
      setLocalLoading(false);
    }
  };

  const modalTitle = getModalTitle(isExistingContact, isFormMode, role);

  const formFooter = [
    <Button key="cancel" onClick={isExistingContact ? () => setIsFormMode(false) : onCancel}>
      {isExistingContact ? 'Volver' : 'Cancelar'}
    </Button>,
    <Popconfirm
      key="confirm"
      title="¿Estás seguro de realizar esta acción?"
      onConfirm={() => form.submit()}
      okText="Sí"
      cancelText="No"
    >
      <Button type="primary" loading={loading}>
        {isExistingContact ? "Actualizar" : "Crear"}
      </Button>
    </Popconfirm>
  ];

  return (
    <Modal
      title={<div style={{ textAlign: "center" }}>{modalTitle}</div>}
      open={visible}
      onCancel={onCancel}
      footer={isFormMode ? formFooter : null}
      maskClosable={false}
      destroyOnHidden
      width={700}
    >
      <Spin spinning={loading}>
        {/* ── View mode ── */}
        {!isFormMode && isExistingContact && initialValues && (
          <ViewContact contact={initialValues} onEdit={() => setIsFormMode(true)} />
        )}

        {/* ── Form mode ── */}
        {isFormMode && (
          <Form
            form={form}
            layout="vertical"
            onFinish={handleFinish}
            initialValues={{ requires_electronic_invoice: false }}
          >
            <Row gutter={16}>
              <Col sm={12} xs={24}>
                <Form.Item
                  label="Nombre"
                  name="name"
                  rules={[{ required: true, message: 'Ingrese un nombre válido' }]}
                >
                  <Input maxLength={100} />
                </Form.Item>
              </Col>

              <Col sm={12} xs={24}>
                <Form.Item
                  label="Tipo de documento"
                  name="document_type"
                  rules={[{ required: validationMode === 'strict', message: 'Seleccione un tipo de documento' }]}
                >
                  <Select placeholder="Selecciona tipo" allowClear>
                    <Option value="CC">Cédula</Option>
                    <Option value="NIT">NIT</Option>
                  </Select>
                </Form.Item>
              </Col>

              <Col sm={12} xs={24}>
                <Form.Item
                  label="Identificación"
                  name="document_number"
                  rules={[
                    { required: validationMode === 'strict', message: 'Ingrese una identificación válida' },
                    { pattern: /^\d+$/, message: 'Solo se permiten números' }
                  ]}
                >
                  <Input maxLength={20} />
                </Form.Item>
              </Col>

              <Col sm={12} xs={24}>
                <Form.Item
                  label="Celular"
                  name="phone"
                  rules={[
                    { required: true, message: 'Ingrese un celular válido' },
                    { pattern: /^\d+$/, message: 'Solo se permiten números' },
                    { len: 10, message: 'Debe tener 10 dígitos' }
                  ]}
                >
                  <Input maxLength={10} />
                </Form.Item>
              </Col>

              <Col sm={12} xs={24}>
                <Form.Item
                  label="Email"
                  name="email"
                  rules={[{ type: 'email', message: 'Ingrese un correo válido' }]}
                >
                  <Input maxLength={100} />
                </Form.Item>
              </Col>

              <Col sm={12} xs={24}>
                <Form.Item label="Dirección" name="address">
                  <Input maxLength={200} />
                </Form.Item>
              </Col>

              <Col span={24}>
                <Form.Item name="requires_electronic_invoice" valuePropName="checked">
                  <Checkbox>Facturación electrónica / Obligaciones tributarias</Checkbox>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        )}
      </Spin>
    </Modal>
  );
};
